import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';

export default function ManageAccessModal({ isOpen, onClose, member, roles, onSuccess }) {
    const { user } = useAuthStore();
    const [formData, setFormData] = useState({ email: '', password: '', role: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState('');
    const [confirmRevoke, setConfirmRevoke] = useState(false);

    useEffect(() => {
        if (member) {
            setFormData({
                email: member.email || '',
                password: '',
                role: member.roles && member.roles.length > 0 ? member.roles[0].slug : ''
            });
            setError(null);
            setSuccessMsg('');
            setConfirmRevoke(false);
        }
    }, [member, isOpen]);

    if (!isOpen || !member) return null;

    const hasAccess = !!member.wp_user_id;

    // Calcula o nível máximo de hierarquia do utilizador logado (níveis menores = maior privilégio, e.g., 1 é admin)
    const hasFullAccess = user?.roles?.some(r => r.permissions && r.permissions.includes('level:full_access'));
    const userMaxHierarchy = user?.roles?.reduce((min, r) => Math.min(min, r.hierarchy_level || 999), 999) || 999;
    
    // Filtra os roles disponíveis: se não for full_access, só pode atribuir roles com nível >= ao seu
    const assignableRoles = hasFullAccess ? roles : roles.filter(r => (r.hierarchy_level || 999) >= userMaxHierarchy);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError(null);
            setSuccessMsg('');

            if (hasAccess) {
                // Atualizar Acesso (Email, Role, Password opcional)
                const payload = { role: formData.role, email: formData.email };
                if (formData.password) payload.password = formData.password;

                await api.put(`/members/${member.id}/access`, payload, {
                    params: { tenant_id: user.tenant_id }
                });
                setSuccessMsg('Acesso atualizado com sucesso!');
            } else {
                // Conceder Acesso
                await api.post(`/members/${member.id}/access`, formData, {
                    params: { tenant_id: user.tenant_id }
                });
                setSuccessMsg('Acesso concedido com sucesso!');
            }

            setTimeout(() => {
                onSuccess();
            }, 1000);

        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Erro ao processar requisição');
        } finally {
            setSaving(false);
        }
    };

    const handleRevoke = async () => {
        try {
            setSaving(true);
            setError(null);
            await api.delete(`/members/${member.id}/access`, {
                params: { tenant_id: user.tenant_id }
            });
            setSuccessMsg('Acesso revogado com sucesso!');
            setTimeout(() => {
                onSuccess();
            }, 1000);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Erro ao revogar acesso');
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay active">
            <div className="modal" style={{ background: 'var(--surface, #ffffff)', color: 'var(--text-primary, #172B4D)', maxWidth: '500px', textAlign: 'left' }}>
                <div className="modal-header">
                    <h2 className="modal-title">{hasAccess ? 'Gerir Acesso' : 'Conceder Acesso'}</h2>
                    <button className="modal-close" onClick={onClose} disabled={saving}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                <div className="modal-body">
                    <p className="mb-4 text-muted" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
                        {hasAccess ? 'Atualize as credenciais e perfil de acesso de ' : 'Defina as credenciais para '} 
                        <strong>{member.full_name}</strong>.
                    </p>

                    {error && (
                        <div className="alert alert-error mb-4" style={{ marginBottom: '1rem' }}>
                            {error}
                        </div>
                    )}

                    {successMsg && (
                        <div className="alert alert-success mb-4" style={{ marginBottom: '1rem' }}>
                            {successMsg}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label className="form-label">Email de Login</label>
                            <input
                                type="email"
                                className="form-input"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                required
                            />
                            <small className="form-hint" style={{ fontSize: '0.8rem', color: '#666' }}>Este será o utilizador de login no sistema.</small>
                        </div>

                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label className="form-label">{hasAccess ? 'Nova Senha (Reset)' : 'Senha Provisória'}</label>
                            <input
                                type="password"
                                className="form-input"
                                value={formData.password}
                                onChange={e => setFormData({ ...formData, password: e.target.value })}
                                required={!hasAccess}
                                minLength="8"
                            />
                            <small className="form-hint" style={{ fontSize: '0.8rem', color: '#666' }}>
                                {hasAccess ? 'Deixe em branco para manter a senha atual. Mínimo 8 caracteres.' : 'No mínimo 8 caracteres.'}
                            </small>
                        </div>

                        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                            <label className="form-label">Perfil / Função (Role)</label>
                            <select
                                className="form-input form-select"
                                value={formData.role}
                                onChange={e => setFormData({ ...formData, role: e.target.value })}
                                required
                                style={{ background: 'white' }}
                            >
                                <option value="">Selecione a função...</option>
                                {assignableRoles.map(r => (
                                    <option key={r.id} value={r.slug}>{r.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="modal-footer" style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={saving}
                                style={{ flex: 1 }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={saving}
                                style={{ flex: 1 }}
                            >
                                {saving ? 'A guardar...' : 'Guardar'}
                            </button>
                        </div>
                    </form>

                    {hasAccess && (
                        <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                            <h3 style={{ fontSize: '1rem', color: 'var(--danger)', marginBottom: '0.5rem' }}>Zona de Perigo</h3>
                            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                                Ao revogar o acesso, este utilizador perderá imediatamente a capacidade de entrar no sistema.
                            </p>
                            
                            {confirmRevoke ? (
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Tem a certeza?</span>
                                    <button 
                                        className="btn btn-danger btn-sm"
                                        onClick={handleRevoke}
                                        disabled={saving}
                                    >
                                        Sim, Revogar
                                    </button>
                                    <button 
                                        className="btn btn-outline btn-sm"
                                        onClick={() => setConfirmRevoke(false)}
                                        disabled={saving}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            ) : (
                                <button 
                                    type="button"
                                    className="btn btn-outline btn-sm text-danger"
                                    onClick={() => setConfirmRevoke(true)}
                                    disabled={saving}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '4px' }}>block</span>
                                    Revogar Acesso
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
